import {forwardRef, useEffect, useMemo} from "react";
import {
    IProtocolStep,
    IGroupStep,
    TProtocolStepId
} from "../../types/protocol";
import {Box, BoxProps, Text} from "@mantine/core";
import {RecorderStepView} from "./RecorderStepView.tsx";
import {IEvent, TEventId, TSubjectId} from "@/common/types";
import cx from "clsx";
import classes from "./RecorderGroupStepsView.module.css";
import {DeepPartial} from "@/utils/types/DeepPartial";
import {IProtocolStepEvent} from "@/protocols/types/IProtocolStepEvent.ts";
import {deriveGroupStatusFromStatuses} from "@/protocols/studies/utils/groupStatus";

interface RecorderGroupStepsViewProps extends BoxProps {
    group: IGroupStep;
    allSteps: Array<IProtocolStep>;
    subjectId: TSubjectId;
    protocolStepEvents: Record<TProtocolStepId, Array<IEvent>>;
    onDeleteEvent: (id: TEventId) => void
    timezone: string;
    onStepChange: (stepId: TProtocolStepId, updates: DeepPartial<IProtocolStep>) => void;
    onStepSelected: (id: TProtocolStepId) => void
    selectedStep: IProtocolStep
    /**
     * Single creation hook forwarded to every member RecorderStepView's
     * EventRecorder. See RecorderStepView for details.
     */
    createEvent?: (
        eventData: IProtocolStepEvent,
    ) => Promise<IProtocolStepEvent>
}


export const RecorderGroupStepsView = forwardRef<HTMLDivElement, RecorderGroupStepsViewProps>(({
                                                                                                   group,
                                                                                                   allSteps,
                                                                                                   subjectId,
                                                                                                   protocolStepEvents,
                                                                                                   onDeleteEvent,
                                                                                                   timezone,
                                                                                                   onStepChange,
                                                                                                   selectedStep,
                                                                                                   className,
                                                                                                   onStepSelected,
                                                                                                   createEvent,
                                                                                                   ...others
                                                                                               }, ref) => {

    // Get member steps in order based on memberStepIds
    const memberSteps = useMemo(() => {
        return (group.memberStepIds || [])
            .map(id => allSteps.find(step => step.id === id))
            .filter((step): step is IProtocolStep => step !== undefined);
    }, [group.memberStepIds, allSteps]);

    // whenever our steps change we re-calculate the group status and update if needed
    useEffect(() => {
        const groupStatus = deriveGroupStatusFromStatuses(memberSteps.map((s) => s.status));
        if (groupStatus !== group.status) {
            onStepChange(group.id, {status: groupStatus});
        }
    }, [group, allSteps])
    return (
        <Box ref={ref} className={cx(classes.root, className)} {...others}>
            <Box className={classes.listContainer}>
                {memberSteps.length === 0 ? (
                    <Box className={classes.emptyState}>
                        <Text c="dimmed" ta="center">
                            No steps in this group.
                        </Text>
                    </Box>
                ) : (
                    <div className={classes.stepsList}>
                        {memberSteps.map((step, index) => (
                            <Box key={`stepView-${step.id}`} onClick={(ev) => ev.stopPropagation()}>
                                <RecorderStepView
                                    key={step.id}
                                    step={step}
                                    index={index}
                                    subjectId={subjectId}
                                    protocolStepEvents={protocolStepEvents}
                                    onDeleteEvent={onDeleteEvent}
                                    timezone={timezone}
                                    selected={step.id === selectedStep.id}
                                    onSelect={() => onStepSelected(step.id)}
                                    onChange={(updates) => onStepChange(step.id, updates)}
                                    createEvent={createEvent}
                                />
                            </Box>
                        ))}
                    </div>
                )}
            </Box>
        </Box>
    );
});